import React, { useMemo, useState } from "react";
import { formatarData, formatarMoeda } from "../../utils/calculos";
import { hasValue } from "../../utils/data";
import { getColunasMaquinas } from "../../utils/industrializacao";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import CardsLinhas, { dataCurta } from "./CardsLinhas";
import {
  OPERACOES_EXIBICAO,
  buildLinhasAmbiente,
  buildLinhasLote,
  ordenarLinhas,
} from "./visoes";
import {
  AguardandoTag,
  CelulaDupla,
  EmptyState,
  LinhaClicavel,
  Progresso,
  ProgressoTrilho,
  StatusBadge,
  Tabela,
  TabelaWrap,
  Td,
  Th,
  ThBotao,
  VisaoTab,
  VisaoTabs,
} from "./styles";

export const MODOS_TABELA = [
  { id: "lote", label: "Por lote" },
  { id: "ambiente", label: "Por ambiente" },
];

function CabecalhoOrdenavel({ coluna, ordem, onOrdenar, children, align, width }) {
  const ativa = ordem?.coluna === coluna;
  const seta = ativa ? (ordem.direcao === "asc" ? "▲" : "▼") : "";
  return (
    <Th
      $align={align}
      style={width ? { width } : undefined}
      aria-sort={ativa ? (ordem.direcao === "asc" ? "ascending" : "descending") : "none"}
    >
      <ThBotao type="button" onClick={() => onOrdenar(coluna)}>
        {children}
        <span aria-hidden>{seta}</span>
      </ThBotao>
    </Th>
  );
}

function BarraProgresso({ feitos, total }) {
  const pct = total ? Math.round((feitos / total) * 100) : 0;
  return (
    <Progresso title={`${feitos} de ${total} ambientes`}>
      <ProgressoTrilho $completo={feitos === total}>
        <div style={{ width: `${pct}%` }} />
      </ProgressoTrilho>
      <small>
        {feitos}/{total}
      </small>
    </Progresso>
  );
}

function DataOuAguardando({ valor }) {
  if (!hasValue(valor)) return <AguardandoTag>Aguardando</AguardandoTag>;
  // "." na planilha = não se aplica a este ambiente.
  if (String(valor).trim() === ".") return <span title="Não se aplica">—</span>;
  return formatarData(valor);
}

function TabelaLotes({ linhas, precosVisiveis, ordem, onOrdenar, onSelectLote }) {
  return (
    <Tabela>
      <thead>
        <tr>
          <CabecalhoOrdenavel coluna="loteLabel" ordem={ordem} onOrdenar={onOrdenar} width="22%">
            Lote / Cliente
          </CabecalhoOrdenavel>
          <Th $align="center" style={{ width: "9%" }} title="Ambientes pendentes / total do lote">
            Pendentes
          </Th>
          {getColunasMaquinas().map((op) => (
            <Th key={op.chave} style={{ width: "11%" }}>
              {op.titulo}
            </Th>
          ))}
          <Th style={{ width: "13%" }}>Etapa atual</Th>
          <CabecalhoOrdenavel coluna="limite" ordem={ordem} onOrdenar={onOrdenar} width="9%">
            Limite
          </CabecalhoOrdenavel>
          <Th $align="center" style={{ width: "13%" }}>
            Status
          </Th>
          {precosVisiveis && (
            <CabecalhoOrdenavel coluna="valor" ordem={ordem} onOrdenar={onOrdenar} align="right" width="10%">
              Valor
            </CabecalhoOrdenavel>
          )}
        </tr>
      </thead>
      <tbody>
        {linhas.map((linha) => (
          <LinhaClicavel key={linha.id} onClick={() => onSelectLote(linha.lote)}>
            <Td>
              <CelulaDupla>
                <strong>{linha.loteLabel}</strong>
                <span title={linha.cliente}>{linha.cliente}</span>
              </CelulaDupla>
            </Td>
            <Td $align="center">{linha.ambientes}</Td>
            {OPERACOES_EXIBICAO.map((op) => (
              <Td key={op.chave}>
                <BarraProgresso {...linha.progresso[op.chave]} />
              </Td>
            ))}
            <Td title={linha.etapa}>{linha.etapa}</Td>
            <Td>{formatarData(linha.limite)}</Td>
            <Td $align="center">
              <StatusBadge $tone={linha.statusVariant}>{linha.status}</StatusBadge>
            </Td>
            {precosVisiveis && <Td $align="right">{formatarMoeda(linha.valor)}</Td>}
          </LinhaClicavel>
        ))}
      </tbody>
    </Tabela>
  );
}

function TabelaAmbientes({ linhas, precosVisiveis, ordem, onOrdenar, onSelectLote }) {
  return (
    <Tabela>
      <thead>
        <tr>
          <CabecalhoOrdenavel coluna="loteLabel" ordem={ordem} onOrdenar={onOrdenar} width="8%">
            Lote
          </CabecalhoOrdenavel>
          <CabecalhoOrdenavel coluna="cliente" ordem={ordem} onOrdenar={onOrdenar} width="20%">
            Cliente / Ambiente
          </CabecalhoOrdenavel>
          <Th style={{ width: "9%" }}>MDF chegou</Th>
          {getColunasMaquinas().map((op) => (
            <Th key={op.chave} style={{ width: "9%" }}>
              {op.titulo}
            </Th>
          ))}
          <Th style={{ width: "13%" }}>Etapa</Th>
          <CabecalhoOrdenavel coluna="limite" ordem={ordem} onOrdenar={onOrdenar} width="9%">
            Limite
          </CabecalhoOrdenavel>
          <Th $align="center" style={{ width: "13%" }}>
            Status do lote
          </Th>
          {precosVisiveis && (
            <CabecalhoOrdenavel coluna="valor" ordem={ordem} onOrdenar={onOrdenar} align="right" width="9%">
              Valor
            </CabecalhoOrdenavel>
          )}
        </tr>
      </thead>
      <tbody>
        {linhas.map((linha) => (
          <LinhaClicavel key={linha.id} onClick={() => onSelectLote(linha.lote)}>
            <Td>
              <strong>{linha.loteLabel.replace("Lote ", "")}</strong>
            </Td>
            <Td>
              <CelulaDupla>
                <strong title={linha.cliente}>{linha.cliente}</strong>
                <span title={linha.ambiente}>{linha.ambiente}</span>
              </CelulaDupla>
            </Td>
            <Td>{formatarData(linha.mdfBordo)}</Td>
            {OPERACOES_EXIBICAO.map((op) => (
              <Td key={op.chave}>
                <DataOuAguardando valor={linha.datas[op.chave]} />
              </Td>
            ))}
            <Td title={linha.etapa}>{linha.etapa}</Td>
            <Td>{formatarData(linha.limite)}</Td>
            <Td $align="center">
              <StatusBadge $tone={linha.statusVariant}>{linha.status}</StatusBadge>
            </Td>
            {precosVisiveis && <Td $align="right">{formatarMoeda(linha.valor)}</Td>}
          </LinhaClicavel>
        ))}
      </tbody>
    </Tabela>
  );
}

// ≤1024px: a linha vira card (mesmo clique → LoteDrawer).
function cardDaLinhaLote(linha) {
  return {
    id: linha.id,
    titulo: linha.loteLabel,
    sub: linha.cliente,
    selo: <StatusBadge $tone={linha.statusVariant}>{linha.status}</StatusBadge>,
    barra: linha.statusVariant,
    meta: [
      ["pend.", linha.ambientes],
      ["etapa", linha.etapa],
      ["limite", dataCurta(linha.limite)],
    ],
  };
}

function cardDaLinhaAmbiente(linha) {
  return {
    id: linha.id,
    titulo: linha.cliente,
    sub: linha.ambiente,
    selo: <StatusBadge $tone={linha.statusVariant}>{linha.status}</StatusBadge>,
    barra: linha.statusVariant,
    meta: [
      ["lote", linha.loteLabel.replace("Lote ", "")],
      ["etapa", linha.etapa],
      ["limite", dataCurta(linha.limite)],
      ["mdf", dataCurta(linha.mdfBordo)],
    ],
  };
}

export function SeletorModoTabela({ modo, onChange }) {
  return (
    <VisaoTabs role="group" aria-label="Linhas da tabela">
      {MODOS_TABELA.map((item) => (
        <VisaoTab
          key={item.id}
          type="button"
          $active={modo === item.id}
          aria-pressed={modo === item.id}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </VisaoTab>
      ))}
    </VisaoTabs>
  );
}

export default function TabelaView({ lotes, modo, precosVisiveis, onSelectLote }) {
  const [ordem, setOrdem] = useState(null);
  const compacto = useMediaQuery(mq.tablet);

  const linhas = useMemo(() => {
    const base = modo === "ambiente" ? buildLinhasAmbiente(lotes) : buildLinhasLote(lotes);
    return ordenarLinhas(base, ordem);
  }, [lotes, modo, ordem]);

  function alternarOrdem(coluna) {
    setOrdem((atual) => {
      if (atual?.coluna !== coluna) return { coluna, direcao: "asc" };
      if (atual.direcao === "asc") return { coluna, direcao: "desc" };
      return null;
    });
  }

  if (linhas.length === 0) {
    return <EmptyState>Nenhum lote pendente nas máquinas com os filtros atuais.</EmptyState>;
  }

  if (compacto) {
    const paraCard = modo === "ambiente" ? cardDaLinhaAmbiente : cardDaLinhaLote;
    return (
      <CardsLinhas
        testId="industrializacao-tabela-cards"
        itens={linhas.map((linha) => ({ ...paraCard(linha), onAbrir: () => onSelectLote(linha.lote) }))}
      />
    );
  }

  const Componente = modo === "ambiente" ? TabelaAmbientes : TabelaLotes;
  return (
    <TabelaWrap>
      <Componente
        linhas={linhas}
        precosVisiveis={precosVisiveis}
        ordem={ordem}
        onOrdenar={alternarOrdem}
        onSelectLote={onSelectLote}
      />
    </TabelaWrap>
  );
}
